import { parseHtml } from '@healthhub/react-ui';
import type { ModalFuncProps } from 'antd';
import { Modal } from 'antd';
import DOMPurify from 'dompurify';

import { CaregiverPermissionErrorModal } from './CaregiverPermissionErrorModal';
import CaregiverRequestFailedModal from './CaregiverRequestFailedModal';
import { ChartSourceModal } from './ChartSourceModal';
import DownloadCalendarModal from './DownloadCalendarModal/DownloadCalendarModal';
import { ErrorModal } from './ErrorModal';
import {
  HealthCaregiverRecordTypeCancelModal,
  HealthCaregiverRecordTypeConfirmModal,
} from './HealthCaregiverRecordTypeModal';
import { MyInfoRetrievalErrorModal } from './MyInfoRetrievalErrorModal';
import NewPatientPromptModal from './NewPatientPromptModal/NewPatientPromptModal';
import { PDFErrorModal } from './PDFErrorModal';
import PeopleUnderMyCareModal from './PeopleUnderMyCareModal';
import { RedirectionModal } from './RedirectionModal';
import { RemoveCaregiverModal } from './RemoveCaregiverModal';
import { RemoveCareRecipientModal } from './RemoveCareRecipientModal';
import { RemoveChildrenModal } from './RemoveChildrenModal';
import { SubsidyForServicesModal } from './SubsidyForServicesModal';
import { SuccessModal } from './SuccessModal';

const defaultModalProps: ModalFuncProps = {
  styles: {
    body: {
      paddingBottom: 0,
      paddingLeft: '1.5rem',
      paddingRight: '1.5rem',
      paddingTop: '1.5rem',
      width: '100%',
    },
  },
  cancelButtonProps: {
    className: 'hidden',
  },
  centered: true,
  icon: null,
  okButtonProps: {
    className: 'hidden',
  },
};

export const showModal = (options: ModalFuncProps) => {
  Modal.confirm({
    ...defaultModalProps,
    ...options,
  });
};

export const showPdfErrorModal = (errorCode: string) => {
  showModal({
    content: <PDFErrorModal errorCode={errorCode} onClose={Modal.destroyAll} />,
  });
};

interface ShowErrorModalProps {
  btnText?: string;
  content?: React.ReactNode | string;
  errorCode?: string;
  hideTitle?: boolean;
  onClose?: () => void;
  showContactInfo?: boolean;
  title?: React.ReactNode | string;
}

export const showErrorModal = ({
  btnText,
  content,
  errorCode,
  hideTitle,
  onClose,
  showContactInfo,
  title,
}: ShowErrorModalProps) => {
  showModal({
    className: '[&_.ant-modal-content]:min-w-[300px] [&_.ant-modal-content]:max-w-[500px]',
    content: (
      <ErrorModal
        btnText={btnText}
        content={content}
        errorCode={errorCode}
        hideTitle={hideTitle}
        onClose={onClose ?? Modal.destroyAll}
        showContactInfo={showContactInfo}
        title={title}
      />
    ),
  });
};

export const showConfirmHealthierSGModal = (onConfirm: () => void) => {
  showModal({
    content: <HealthCaregiverRecordTypeConfirmModal onCancel={Modal.destroyAll} onConfirm={onConfirm} />,
  });
};

export const showCancelHealthierSGModal = (onConfirm: () => void) => {
  showModal({
    content: <HealthCaregiverRecordTypeCancelModal onCancel={Modal.destroyAll} onConfirm={onConfirm} />,
  });
};

export const showSuccessModal = ({ children, okText, onOk, className = '' }) => {
  showModal({
    content: (
      <SuccessModal okText={okText} onOk={onOk}>
        {children}
      </SuccessModal>
    ),
    className,
  });
};

export const showRemoveCaregiverModal = (onConfirm: () => void) => {
  showModal({
    content: <RemoveCaregiverModal onCancel={Modal.destroyAll} onConfirm={onConfirm} />,
  });
};

export const showRemoveCarecipientModal = (onConfirm: () => void) => {
  showModal({
    content: <RemoveCareRecipientModal onCancel={Modal.destroyAll} onConfirm={onConfirm} />,
  });
};

export const showRemoveChildrenModal = (onConfirm: () => Promise<void>) => {
  showModal({
    content: <RemoveChildrenModal onCancel={Modal.destroyAll} onConfirm={onConfirm} />,
  });
};

export const showChartSourceModal = () => {
  showModal({
    content: <ChartSourceModal />,
  });
};

export const showRedirectionModal = (props: {
  actualUrl?: string;
  description?: string;
  title?: string;
  url: string;
}) => {
  showModal({
    content: <RedirectionModal {...props} />,
  });
};

export const showRetrieveMyInfoErrorModal = () => {
  showModal({
    content: <MyInfoRetrievalErrorModal />,
  });
};

export const showSubsidyForServicesModal = () => {
  showModal({
    content: <SubsidyForServicesModal />,
    width: 750,
  });
};

export const showPeopleUnderMyCareModal = () => {
  showModal({
    styles: {
      body: {
        paddingBottom: 0,
        paddingLeft: '1rem',
        paddingRight: '1rem',
        paddingTop: '1.5rem',
        width: '100%',
      },
    },
    className: '[&_.ant-modal-confirm-content]:min-w-[300px] [&_.ant-modal-confirm-content]:mt-0',
    content: <PeopleUnderMyCareModal />,
  });
};

export const showNewPatientPromptModal = (
  handleDoLater: () => void,
  handleProceed: () => void,
  isLiveChatEnabled?: boolean
) => {
  showModal({
    content: (
      <NewPatientPromptModal
        isLiveChatEnabled={isLiveChatEnabled}
        onClickCancel={handleDoLater}
        onClickOk={handleProceed}
      />
    ),
  });
};

export const showCalendarDownloadModal = () => {
  showModal({
    content: <DownloadCalendarModal />,
  });
};

export const showCaregiverRequestFailedModal = () => {
  showModal({
    content: (
      <ErrorModal
        btnText="OK"
        content={<CaregiverRequestFailedModal />}
        onClose={Modal.destroyAll}
        title="Request Unsuccesful"
      />
    ),
  });
};

export const showCaregiverPermissionErrorModal = ({ btnText, title }: { btnText: string; title: string }) => {
  showModal({
    content: (
      <ErrorModal
        btnText={btnText}
        content={<CaregiverPermissionErrorModal />}
        onClose={Modal.destroyAll}
        title={title}
      />
    ),
  });
};

export const getErrorModalContentFromApiResponse = (apiResponse: any) => {
  const code = apiResponse?.Code ?? apiResponse?.code ?? '';

  // Prefer `Message` from API payload (mock shape). Remove appended internal error code
  // that `parseResponse` concatenates (e.g. "... Please try again later. (LB0004)").
  let apiMessage = apiResponse?.Message ?? apiResponse?.message ?? apiResponse?.response?.data?.Message ?? '';
  if (apiMessage) {
    apiMessage = apiMessage.replace(/\s*\([^)]*\)\s*$/, '').trim();
  }
  const renderedServiceText = apiMessage ? parseHtml(DOMPurify.sanitize(apiMessage)) : '';

  const content = (
    <div className="text-center">
      <div className="mb-2">{renderedServiceText}</div>
    </div>
  );

  return { content, code };
};
